import { makeStyles } from '@material-ui/core'
import { Handle, Position } from '@xyflow/react'
import clsx from 'clsx'
import React, { useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { nodePropTypes } from '../../../Canvas/nodes/constants'
import { ICON_NAMES } from '../../../../../../constants'
import Icon from '../../../../../atoms/Icon'
import { useGetNodeValues } from '../../../Canvas/nodes/hooks/useGetNodeValues'
import { useEntityGraphContext } from '../../../Wrapper/EntityGraphProvider'
import SydModal from '../../../../../commonDesign/SydModal'
import EditForm from './editForms'

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    '& .__edit-toggle': {
      opacity: 0,
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'var(--color-primary-400)',
      color: 'var(--color-contrast-primary-400)',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      maskSize: 'contain',
      mask: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><polygon points=\'50,6 94,28 94,72 50,94 6,72 6,28\' fill=\'white\' stroke=\'white\' stroke-width=\'12\' stroke-linejoin=\'round\' stroke-linecap=\'round\'/></svg>") center/contain no-repeat',
      WebkitMask: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><polygon points=\'50,6 94,28 94,72 50,94 6,72 6,28\' fill=\'white\' stroke=\'white\' stroke-width=\'12\' stroke-linejoin=\'round\' stroke-linecap=\'round\'/></svg>") center/contain no-repeat',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      WebkitMaskSize: 'contain',
      zIndex: 10,
      width: '2.5em',
      height: '2.5em',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'opacity 0.3s'
    },
    '&:hover .__edit-toggle': {
      opacity: 1
    }
  },
  slot: ({ size }) => ({
    border: '1px solid var(--color-neutral-300)',
    boxShadow: 'var(--shadow-sm)',
    backgroundColor: 'var(--color-neutral-50)',
    borderRadius: 'var(--border-radius-md)',
    padding: '1.25em',
    background: 'var(--theme-color-surface)',
    color: 'var(--text-color-secondary)',
    minWidth: size === 'lg' ? '25em' : size === 'sm' ? '16em' : '20em',
    minHeight: size === 'lg' ? '6em' : size === 'sm' ? '4em' : '5em',
    fontSize: size === 'sm' ? '0.75rem' : size === 'lg' ? '1.25rem' : '1rem'
  }),
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--dim-2)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    textAlign: 'left'
  },
  hexagonIcon: {
    width: '3em',
    height: '3em',
    color: 'var(--color-contrast-primary-500)',
    background: 'var(--color-primary-500)',
    overflow: 'hidden',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain',
    mask: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><polygon points=\'50,6 94,28 94,72 50,94 6,72 6,28\' fill=\'white\' stroke=\'white\' stroke-width=\'12\' stroke-linejoin=\'round\' stroke-linecap=\'round\'/></svg>") center/contain no-repeat',
    WebkitMask: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><polygon points=\'50,6 94,28 94,72 50,94 6,72 6,28\' fill=\'white\' stroke=\'white\' stroke-width=\'12\' stroke-linejoin=\'round\' stroke-linecap=\'round\'/></svg>") center/contain no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    WebkitMaskSize: 'contain',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&.__empty': {
      background: 'var(--color-neutral-200)',
      color: 'var(--color-neutral-500)'
    },
    '&.__filledIcon': {
      position: 'absolute',
      top: '-1.5em',
      left: '-1.5em'
    }
  },
  title: {
    fontWeight: 600,
    fontSize: '1.3em'
  },
  subtitle: {
    fontWeight: 300,
    fontSize: '.8em'
  },
  description: {
    '&.__incomplete': {
      opacity: 0.6
    }
  },
  markdownDisplay: {
    textAlign: 'left',
    width: '100%',
    '& ul': {
      paddingLeft: '0.5em'
    },
    '& li+li': {
      marginTop: '0.5em'
    }
  }
}))

const handlePositions = [
  { id: 'top', style: { opacity: 0, top: 0, left: '50%' }, position: Position.Top },
  { id: 'right', style: { opacity: 0, top: '50%', right: 0 }, position: Position.Right },
  { id: 'bottom', style: { opacity: 0, top: '100%', left: '50%' }, position: Position.Bottom },
  { id: 'left', style: { opacity: 0, top: '50%', left: 0 }, position: Position.Left },
  { id: 'rightTop', style: { opacity: 0, top: '25%', right: 0 }, position: Position.Right },
  { id: 'rightBottom', style: { opacity: 0, top: '75%', right: 0 }, position: Position.Right },
  { id: 'leftBottom', style: { opacity: 0, top: '75%', left: 0 }, position: Position.Left },
  { id: 'leftTop', style: { opacity: 0, top: '25%', left: 0 }, position: Position.Left }
]

export default function LifeMapNode ({ id, data, selected }) {
  const size = data?.nodeProps?.size || 'md'

  const classes = useStyles({ size })
  const { state, dispatch } = useEntityGraphContext()

  const {
    header,
    description,
    backgroundColor
  } = useGetNodeValues(data, state.linkedEntityData)

  const itemData = useMemo(() => data?.entityProps?.data, [data])
  const isSlot = useMemo(() => data.isSlot, [data.isSlot])
  const isIncomplete = useMemo(() => isSlot && !data?.entityProps?.levelId, [data?.entityProps?.levelId, isSlot])
  const canEditContent = useMemo(() => !!data.entityProps?.entityLevelTypeId && state.config.lifeMap?.canEditSlotContent, [data.entityProps?.entityLevelTypeId, state.config.lifeMap?.canEditSlotContent])
  const key = useMemo(() => state.linkedEntityData?.[data?.entityProps?.levelTypeId]?.[data?.entityProps?.levelId] ?? data?.entityProps?.levelId ?? 'lifeMapNode', [data?.entityProps, state.linkedEntityData])

  const [isEditing, setIsEditing] = useState(false)
  const onSubmitChange = (updatedData) => {
    setIsEditing(false)
    dispatch({
      type: 'UPDATE_NODE',
      payload: {
        nodeId: id,
        path: ['data', 'entityProps'],
        value: {
          ...data.entityProps,
          ...updatedData
        }
      }
    })
  }

  return (
    <>
      {canEditContent && isSlot && (
        <SydModal
          open={isEditing}
          onClose={() => setIsEditing(false)}
          title={`Edit "${header}"`}
          maxWidth='md'
          fullWidth
          showCloseButton
          disablePortal={false}
        >
          <EditForm
            defaultValues={{
              ...data.entityProps,
              chartLevel: {
                levelId: state.levelId,
                levelTypeId: state.levelTypeId
              }
            }}
            levelTypeId={data.entityProps.entityLevelTypeId}
            onSubmit={onSubmitChange}
            onCancel={() => setIsEditing(false)}
          />
        </SydModal>
      )}
      <div key={key} className={clsx(classes.container, { selected })}>
        {canEditContent && isSlot && (
          <button onClick={() => setIsEditing(!isEditing)} className='__edit-toggle'>
            <Icon name={ICON_NAMES.edit} />
          </button>
        )}

        {handlePositions.map(({ position, style, id }) => (
          <Handle key={id} id={`handle_${id}`} type='source' position={position} style={style ?? {}} />
        ))}
        {handlePositions.map(({ position, style, id }) => (
          <Handle key={id} id={`target_${id}`} type='target' position={position} style={style ?? {}} />
        ))}
        <div className={clsx(classes.slot)}>
          <div className={clsx(classes.hexagonIcon, '__filledIcon')} style={{ backgroundColor }}>
            <Icon name={ICON_NAMES.homeUser} customSize={size === 'lg' ? 24 : size === 'sm' ? 14 : 18} />
          </div>
          <div className={classes.header}>
            <div>
              <div className={classes.title}>{header}</div>
              <div className={classes.subtitle}>Last updated 4 weeks ago</div>
            </div>
          </div>
          {description && (
            itemData?.contentType === 'markdown' ? (
              <ReactMarkdown
                className={clsx(classes.markdownDisplay, classes.description, isIncomplete && '__incomplete')}
                remarkPlugins={[remarkGfm]}
              >
                {description}
              </ReactMarkdown>
            ) : (
              <p className={clsx(classes.description, isIncomplete && '__incomplete')}>{description}</p>
            )
          )}
        </div>
      </div>
    </>
  )
}

LifeMapNode.propTypes = nodePropTypes
